import type { SummaryImage } from '@/lib/media/summary-images';
import { toTrimmedNonEmptyString } from '@/lib/utils/type-guards';

export const DEFAULT_TWEET_SUMMARY_INSTRUCTION =
  'You summarize long-form tweets for busy social media managers. Write at most two concise sentences that capture tone, key claims, and any calls to action. Only surface details that matter to the narrative; skip generic notes about attachments or link counts. Avoid emojis, bullet lists, and hashtags, and never invent details.';

export const DEFAULT_TWEET_EXPLANATION_INSTRUCTION =
  'You explain tweets for curious readers. Provide a clear, neutral explanation that covers why the tweet matters, any relevant context or background details, and how the audience should interpret the claims. Reference notable facts or timelines when necessary and avoid repeating the tweet verbatim.';

interface BuildTweetPromptParameters {
  authorName?: string;
  authorUsername?: string;
  createdAtIso: string;
  customPrompt?: string;
  fallbackInstruction: string;
  images?: SummaryImage[];
  tweetText: string;
}

export function buildTweetPrompt({
  authorUsername,
  authorName,
  createdAtIso,
  tweetText,
  images,
  customPrompt,
  fallbackInstruction,
}: BuildTweetPromptParameters): string {
  const lines: string[] = [];
  if (authorUsername) {
    const nameSegment = authorName && authorName !== authorUsername ? ` (${authorName})` : '';
    lines.push(`Author: @${authorUsername}${nameSegment}`);
  }

  lines.push(`Posted: ${createdAtIso}`);

  const header = lines.join('\n');
  const imageNotes = Array.isArray(images)
    ? images.flatMap((image, index) => {
        const alt = toTrimmedNonEmptyString(image.altText);
        if (!alt) {
          return [];
        }
        return [`Image ${index + 1}: ${alt}`];
      })
    : [];
  const imageContextSection = imageNotes.length > 0 ? `\nAttached image descriptions:\n${imageNotes.join('\n')}` : '';

  const trimmedCustom = toTrimmedNonEmptyString(customPrompt);
  const instruction = trimmedCustom ?? fallbackInstruction;

  return `${instruction}

${header}
${imageContextSection}

Tweet content:
"""
${tweetText}
"""`;
}
